// Drives the pending_audio queue: drains retryable entries on app mount,
// `online`, and `visibilitychange` (visible). iOS Safari has no Background
// Sync so we trigger on every chance we get; a re-entrancy guard prevents
// overlapping drains from stepping on each other.

import {
  computeBackoffMs,
  listRetryable,
  markDone,
  markFailed,
  markUploading,
  purgeDone,
  type PendingAudio,
} from './audioQueue'
import { isRetryableError, uploadAudio } from './uploader'
import { ApiClientError } from '@/lib/api'

type DrainListeners = {
  onStart?: (entry: PendingAudio) => void
  onSuccess?: (entry: PendingAudio, audioId: number) => void
  onFailure?: (entry: PendingAudio, err: unknown) => void
}

let draining = false
let listeners: DrainListeners = {}

export function setOrchestratorListeners(l: DrainListeners): void {
  listeners = l
}

/** Drain the queue once. Safe to call repeatedly — overlapping calls no-op. */
export async function drainOnce(): Promise<void> {
  if (draining) return
  draining = true
  try {
    const entries = await listRetryable()
    for (const entry of entries) {
      await attempt(entry)
    }
    // Housekeeping: drop `done` entries older than 1h so IndexedDB stays small.
    await purgeDone()
  } finally {
    draining = false
  }
}

async function attempt(entry: PendingAudio): Promise<void> {
  const claimed = await markUploading(entry.id)
  if (!claimed) return
  listeners.onStart?.(claimed)
  try {
    const res = await uploadAudio({
      visitId: claimed.visitId,
      blob: claimed.blob,
      mimeType: claimed.mimeType,
    })
    await markDone(claimed.id, res.audio.id)
    listeners.onSuccess?.(claimed, res.audio.id)
  } catch (err) {
    if (!isRetryableError(err)) {
      // Permanent failure (4xx except 408/429). Keep row in `failed` so the
      // UI can surface the error instead of silently dropping the recording.
      await markFailed(
        claimed.id,
        err instanceof ApiClientError ? `${err.code}: ${err.message}` : String(err),
        24 * 60 * 60 * 1000, // effectively pause — won't auto-retry for 24h
      )
      listeners.onFailure?.(claimed, err)
      return
    }
    const backoff = computeBackoffMs(claimed.attempts)
    await markFailed(
      claimed.id,
      err instanceof ApiClientError ? `${err.code}: ${err.message}` : String(err),
      backoff,
    )
    listeners.onFailure?.(claimed, err)
  }
}

let installed = false

export function installRetryTriggers(): () => void {
  if (installed) return () => {}
  installed = true

  const onOnline = () => void drainOnce()
  const onVisible = () => {
    if (document.visibilityState === 'visible') void drainOnce()
  }

  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisible)

  // App mount — fire once immediately (non-blocking).
  void drainOnce()

  return () => {
    installed = false
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
